import React, { useState, useEffect, useMemo } from 'react';
import './App.css';

// --- CONFIGURATION ---
const ROLES = ["Top", "Jungle", "Mid", "ADC", "Support"];
const getRoleIconPath = (role) => process.env.PUBLIC_URL + `/icons/${role}.png`;

// Config Ranks
const TIERS = ["IRON", "BRONZE", "SILVER", "GOLD", "PLATINUM", "EMERALD", "DIAMOND", "MASTER", "GRANDMASTER", "CHALLENGER"];
const DIVISIONS = ["IV", "III", "II", "I"];

function App() {
  const [allChampions, setAllChampions] = useState([]);
  const [history, setHistory] = useState([]);

  // États Modals
  const [isModalOpen, setModalOpen] = useState(false);
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [isRankOpen, setIsRankOpen] = useState(false);

  const [isRolling, setIsRolling] = useState(false);

  // États de draft
  const [draftResult, setDraftResult] = useState(null);
  const [banOffset, setBanOffset] = useState(1);
  const [displayedChamps, setDisplayedChamps] = useState({});
  const [version, setVersion] = useState("14.1.1");

  // États de saisie
  const [inputs, setInputs] = useState({ Top: "", Jungle: "", Mid: "", ADC: "", Support: "" });
  const [myMatch, setMyMatch] = useState({ result: null, role: null, champion: "" });
  const [suggestions, setSuggestions] = useState({ field: null, list: [] });

  // État Login & Rank
  const [userRiotId, setUserRiotId] = useState(null);
  const [loginInput, setLoginInput] = useState("");
  const [userRank, setUserRank] = useState({ tier: "SILVER", division: "IV", lp: 0 });
  const [lpChangeInput, setLpChangeInput] = useState(20);

  // Filtre Historique
  const [historyFilter, setHistoryFilter] = useState(null);

  // --- AUDIO ENGINE ---
  const playUiSound = (type) => {
    try {
      const audio = new Audio(process.env.PUBLIC_URL + `/sounds/${type}.mp3`);
      audio.volume = 0.2;
      audio.play().catch(e => console.log("Audio play blocked", e));
    } catch (e) {
      console.error("Erreur son", e);
    }
  };

  const playChampionVoice = (championId) => {
    if (!championId) return;
    const champData = allChampions.find(c => c.id === championId);
    if (!champData) return;
    const voiceUrl = `https://raw.communitydragon.org/latest/plugins/rcp-be-lol-game-data/global/default/v1/champion-choose-vo/${champData.key}.ogg`;
    try {
      const audio = new Audio(voiceUrl);
      audio.volume = 0.4;
      audio.play().catch(e => console.log("Voice play blocked", e));
    } catch (e) {
      console.error("Erreur voix", e);
    }
  };

  // 1. Init
  useEffect(() => {
    fetch('https://ddragon.leagueoflegends.com/api/versions.json')
      .then(res => res.json())
      .then(versions => {
        setVersion(versions[0]);
        fetch(`https://ddragon.leagueoflegends.com/cdn/${versions[0]}/data/fr_FR/champion.json`)
          .then(r => r.json())
          .then(data => {
            const list = Object.values(data.data).sort((a, b) => a.name.localeCompare(b.name));
            setAllChampions(list);
          });
      })
      .catch(err => console.error("Erreur API Riot", err));

    const saved = localStorage.getItem('nemesisHistory');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        const cleanHistory = parsed.map(item => {
          if (item.enemyTeam) return item;
          return {
            enemyTeam: {
              Top: item.Top || "Garen",
              Jungle: item.Jungle || "Garen",
              Mid: item.Mid || "Garen",
              ADC: item.ADC || "Garen",
              Support: item.Support || "Garen"
            },
            userRole: null, userChamp: null, userResult: null,
            date: new Date().toISOString()
          };
        });
        setHistory(cleanHistory);
      } catch (e) { setHistory([]); }
    }

    const savedId = localStorage.getItem('nemesisRiotId');
    if (savedId) setUserRiotId(savedId);

    const savedRank = localStorage.getItem('nemesisUserRank');
    if (savedRank) setUserRank(JSON.parse(savedRank));

  }, []);

  useEffect(() => {
    localStorage.setItem('nemesisUserRank', JSON.stringify(userRank));
  }, [userRank]);

  // Logique Rank
  const handleLpChange = (amount) => {
    let currentTierIdx = TIERS.indexOf(userRank.tier);
    let currentDivIdx = DIVISIONS.indexOf(userRank.division);
    let currentLp = parseInt(userRank.lp);
    let change = parseInt(amount);

    let newLp = currentLp + change;

    if (newLp >= 100) {
      newLp -= 100;
      if (currentDivIdx === 3) {
        if (currentTierIdx < TIERS.length - 1) {
          currentTierIdx++;
          currentDivIdx = 0;
        } else {
          newLp += 100;
        }
      } else {
        currentDivIdx++;
      }
    } else if (newLp < 0) {
      if (currentDivIdx === 0) {
        if (currentTierIdx > 0) {
          currentTierIdx--;
          currentDivIdx = 3;
          newLp = 75;
        } else {
          newLp = 0;
        }
      } else {
        currentDivIdx--;
        newLp = 75;
      }
    }

    setUserRank({
      tier: TIERS[currentTierIdx],
      division: DIVISIONS[currentDivIdx],
      lp: newLp
    });
  };

  const stats = useMemo(() => {
    const total = history.length;
    if (total === 0) return { total: 0, winRate: 0, wins: 0, losses: 0 };
    const validGames = history.filter(h => h.userResult);
    const wins = validGames.filter(h => h.userResult === 'Win').length;
    const losses = validGames.filter(h => h.userResult === 'Loss').length;
    const winRate = validGames.length > 0 ? Math.round((wins / validGames.length) * 100) : 0;
    return { total, wins, losses, winRate };
  }, [history]);

  const handleLogin = () => {
    if(!loginInput) return;
    setUserRiotId(loginInput);
    localStorage.setItem('nemesisRiotId', loginInput);
    setIsLoginOpen(false);
  };

  const handleLogout = () => {
    if(window.confirm("Se déconnecter ?")) {
      setUserRiotId(null);
      localStorage.removeItem('nemesisRiotId');
    }
  };

  const openExternalLink = (type) => {
    if(!userRiotId) { setIsLoginOpen(true); return; }
    const safeId = userRiotId.replace('#', '-');
    let url = "";
    if(type === 'history') url = `https://dpm.lol/${safeId}`;
    if(type === 'stats') url = `https://dpm.lol/${safeId}/champions?queue=solo`;
    if(type === 'live') url = `https://dpm.lol/${safeId}/live`;
    if(url) window.open(url, '_blank');
  };

  const openDpm = (champName, champId) => {
    if(!champName) return;
    if(champId) playChampionVoice(champId);
    const cleanName = champName.replace(/['\s\.]/g, "");
    setTimeout(() => {
       window.open(`https://dpm.lol/champions/${cleanName}/build`, '_blank');
    }, 100);
  };

  const exportData = () => {
    const dataStr = JSON.stringify(history, null, 2);
    const blob = new Blob([dataStr], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `nemesis-history-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const importData = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const json = JSON.parse(e.target.result);
        if (Array.isArray(json)) {
          setHistory(json);
          localStorage.setItem('nemesisHistory', JSON.stringify(json));
          alert("Historique importé !");
        } else {
          alert("Format invalide.");
        }
      } catch (error) { alert("Erreur."); }
    };
    reader.readAsText(file);
  };

  const handleEnemyInput = (role, value) => {
    setInputs({ ...inputs, [role]: value });
    updateSuggestions(role, value);
  };
  const handleMyChampInput = (value) => {
    setMyMatch({ ...myMatch, champion: value });
    updateSuggestions('myChamp', value);
  };
  const updateSuggestions = (field, value) => {
    if (value.length > 0) {
      const match = allChampions.filter(c => c.name.toLowerCase().includes(value.toLowerCase()));
      setSuggestions({ field, list: match });
    } else { setSuggestions({ field: null, list: [] }); }
  };
  const selectSuggestion = (field, champ) => {
    if (field === 'myChamp') setMyMatch({ ...myMatch, champion: champ.name });
    else setInputs({ ...inputs, [field]: champ.name });
    setSuggestions({ field: null, list: [] });
  };
  const hasDuplicates = () => {
    const values = Object.values(inputs).filter(v => v !== "");
    return new Set(values).size !== values.length;
  };

  const saveHistory = () => {
    let isValid = true;
    const entry = { enemyTeam: {}, userRole: myMatch.role, userChamp: null, userResult: myMatch.result, date: new Date().toISOString() };
    ROLES.forEach(r => {
      if(!inputs[r]) isValid = false;
      const found = allChampions.find(c => c.name === inputs[r]);
      entry.enemyTeam[r] = found ? found.id : inputs[r];
    });
    if(!isValid) { alert("Merci de remplir les 5 champions adverses !"); return; }
    if(hasDuplicates()) { alert("Doublons détectés !"); return; }
    if(myMatch.champion) {
      const foundMy = allChampions.find(c => c.name === myMatch.champion);
      entry.userChamp = foundMy ? foundMy.id : myMatch.champion;
    }

    if(myMatch.result && lpChangeInput > 0) {
      const multiplier = myMatch.result === 'Win' ? 1 : -1;
      handleLpChange(lpChangeInput * multiplier);
    }

    const newHistory = [entry, ...history];
    setHistory(newHistory);
    localStorage.setItem('nemesisHistory', JSON.stringify(newHistory));
    setInputs({ Top: "", Jungle: "", Mid: "", ADC: "", Support: "" });
    setMyMatch({ result: null, role: null, champion: "" });
  };

  const deleteHistoryItem = (index) => {
    const newHistory = [...history];
    newHistory.splice(index, 1);
    setHistory(newHistory);
    localStorage.setItem('nemesisHistory', JSON.stringify(newHistory));
  };

  const startDraft = () => {
    if (history.length === 0) return;
    setIsRolling(true);
    setBanOffset(1);
    setDraftResult(null);
    const lastGame = history[0];
    let availableRoles = [...ROLES];
    if (lastGame && lastGame.userRole && ROLES.includes(lastGame.userRole)) {
      availableRoles = availableRoles.filter(r => r !== lastGame.userRole);
    }
    const interval = setInterval(() => {
      const randDisplay = {};
      ROLES.forEach(r => {
        if(allChampions.length > 0) {
          const rand = allChampions[Math.floor(Math.random() * allChampions.length)];
          randDisplay[r] = rand.id;
        }
      });
      setDisplayedChamps(randDisplay);
    }, 80);
    setTimeout(() => {
      clearInterval(interval);
      finalizeDraft(1, availableRoles);
      setIsRolling(false);
    }, 2000);
  };

  const finalizeDraft = (offset, rolePool = ROLES) => {
    const shuffled = [...rolePool].sort(() => 0.5 - Math.random());
    const main = shuffled[0];
    const second = shuffled[1];
    setDraftResult({ main, second });
    if (history.length >= offset) {
      const targetGame = history[offset - 1];
      if(targetGame && targetGame.enemyTeam) setDisplayedChamps(targetGame.enemyTeam);
    }

    // SON DE FIN
    playUiSound('lock');
  };

  const handleBan = () => {
    if(history.length > banOffset) {
      const nextOffset = banOffset + 1;
      setBanOffset(nextOffset);
      const targetGame = history[nextOffset - 1];
      if(targetGame && targetGame.enemyTeam) setDisplayedChamps(targetGame.enemyTeam);
    }
  };

  const getUrlId = (id) => {
    if(!id) return null;
    if(id === "Wukong") return "MonkeyKing";
    return id.replace(/['\s\.]/g, "");
  };

  const filteredHistory = useMemo(() => {
    if(!historyFilter) return history;
    return history.filter(game => game.userRole === historyFilter);
  }, [history, historyFilter]);

  const toggleFilter = (role) => {
    setHistoryFilter(prev => prev === role ? null : role);
  };

  return (
    <div className="app-container">

      {/* HEADER FIXE */}
      <header>
        <div className="header-brand">
          <div className="brand-logo">NEMESIS DRAFT</div>
          <div className="header-separator"></div>
        </div>

        <nav className="header-nav">
          <span className="nav-link active">Draft</span>
          {/* CORRECTION ICI : LIENS EXTERNES */}
          <span className="nav-link" onClick={() => openExternalLink('history')}>Historique</span>
          <span className="nav-link" onClick={() => openExternalLink('stats')}>Stats</span>
          <span className="nav-link" onClick={() => openExternalLink('live')} style={{color: '#ff4444'}}>LIVE</span>
        </nav>

        <div className="header-right">
           {userRiotId && (
             <div className="header-btn-style" onClick={() => setIsRankOpen(true)} title="Modifier mon Rang">
                <img
                  src={`https://raw.communitydragon.org/latest/plugins/rcp-fe-lol-static-assets/global/default/images/ranked-emblem/emblem-${userRank.tier ? userRank.tier.toLowerCase() : 'silver'}.png`}
                  className="rank-icon"
                  alt="Rank"
                />
                <span>{userRank.division} - {userRank.lp} LP</span>
             </div>
           )}

           {userRiotId ? (
             <button className="header-btn-style" onClick={handleLogout} title="Se déconnecter">
                {userRiotId}
             </button>
           ) : (
             <button className="header-btn-style" onClick={() => setIsLoginOpen(true)}>
                CONNEXION
             </button>
           )}
        </div>
      </header>

      <div className="draft-stage">
        {ROLES.map(role => {
          const rawId = displayedChamps[role];
          const urlId = getUrlId(rawId);
          let displayName = rawId || role;
          const data = allChampions.find(c => c.id === rawId);
          if (data) displayName = data.name;

          let isActive = false;
          let isInactive = !isRolling && draftResult;
          let badge = "";

          if (!isRolling && draftResult) {
            if (draftResult.main === role) { isActive = true; isInactive = false; badge = "Principal"; }
            else if (draftResult.second === role) { isActive = true; isInactive = false; badge = "Secondaire"; }
          }

          return (
            <div
              key={role}
              className={`card ${isActive ? 'active' : ''} ${isInactive ? 'inactive' : ''} ${urlId ? 'has-champion' : ''}`}
              onClick={() => { if(urlId) openDpm(displayName, rawId); }}
            >
              {badge && <div className="role-badge">{badge}</div>}
              {urlId ? (
                <img className="card-img" src={`https://ddragon.leagueoflegends.com/cdn/img/champion/loading/${urlId}_0.jpg`} alt={displayName} onError={(e) => {e.target.style.display='none';}} />
              ) : (
                <div className="card-placeholder"><img src={getRoleIconPath(role)} alt={role} className="placeholder-icon" /></div>
              )}
              <div className="card-footer">
                <div className="champion-name">{urlId ? displayName : "---"}</div>
                <div className="role-container">
                  <img src={getRoleIconPath(role)} alt={role} className="role-icon-img" />
                  <span className="role-name">{role}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="controls">
        <button className="btn" onClick={() => setModalOpen(true)}>Éditer Adversaires</button>
        <button className="btn primary" onClick={startDraft} disabled={history.length === 0 || isRolling}>
          {isRolling ? "Draft en cours..." : "Lancer le Tirage"}
        </button>
        {draftResult && history.length > banOffset && (
          <button className="btn ban" onClick={handleBan}>🚫 Banni ? (Essayer Game -{banOffset + 1})</button>
        )}
      </div>

      {isLoginOpen && (
        <div className="modal-overlay" onClick={(e) => { if(e.target.className === 'modal-overlay') setIsLoginOpen(false)}}>
          <div className="modal-content small-modal">
             <div className="modal-header"><h3 className="modal-title">Connexion</h3><button className="close-btn" onClick={() => setIsLoginOpen(false)}>×</button></div>
             <div style={{padding:'20px 0'}}>
                <p style={{color:'#999', fontSize:'0.9rem', marginBottom:'20px'}}>Entrez votre Riot ID (ex: Pseudo#TAG).</p>
                <div className="input-row"><input className="champion-input" value={loginInput} onChange={(e) => setLoginInput(e.target.value)} placeholder="Riot ID..." autoFocus /></div>
                <button className="btn primary" style={{width:'100%', marginTop:'10px'}} onClick={handleLogin}>Valider</button>
             </div>
          </div>
        </div>
      )}

      {isRankOpen && (
        <div className="modal-overlay" onClick={(e) => { if(e.target.className === 'modal-overlay') setIsRankOpen(false)}}>
          <div className="modal-content small-modal">
             <div className="modal-header"><h3 className="modal-title">Édition Manuelle Rang</h3><button className="close-btn" onClick={() => setIsRankOpen(false)}>×</button></div>
             <div style={{padding:'10px 0', textAlign:'left'}}>
                <div className="input-row">
                  <label className="input-label">Tier</label>
                  <select className="champion-input" value={userRank.tier} onChange={(e) => setUserRank({...userRank, tier: e.target.value})}>{TIERS.map(t => <option key={t} value={t}>{t}</option>)}</select>
                </div>
                <div className="input-row">
                  <label className="input-label">Division</label>
                  <select className="champion-input" value={userRank.division} onChange={(e) => setUserRank({...userRank, division: e.target.value})}>{DIVISIONS.map(d => <option key={d} value={d}>{d}</option>)}</select>
                </div>
                <div className="input-row">
                  <label className="input-label">LP Actuels</label>
                  <input type="number" className="champion-input" value={userRank.lp} onChange={(e) => setUserRank({...userRank, lp: parseInt(e.target.value) || 0})} />
                </div>
             </div>
          </div>
        </div>
      )}

      {isModalOpen && (
        <div className="modal-overlay" onClick={(e) => { if(e.target.className === 'modal-overlay') setModalOpen(false)}}>
          <div className="modal-content">
            <div className="modal-header">
              <h2 className="modal-title">Mon Espace de Jeu</h2>
              <button className="close-btn" onClick={() => setModalOpen(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-column">
                <div className="my-match-section">
                  <div className="section-title">Ma Dernière Partie</div>
                  <div className="result-toggle">
                    <button className={`toggle-btn win ${myMatch.result === 'Win' ? 'selected' : ''}`} onClick={() => setMyMatch({...myMatch, result: 'Win'})}>Victoire</button>
                    <button className={`toggle-btn loss ${myMatch.result === 'Loss' ? 'selected' : ''}`} onClick={() => setMyMatch({...myMatch, result: 'Loss'})}>Défaite</button>
                  </div>
                  <div className="input-row">
                    <label className="input-label">Rôle Joué</label>
                    <select className="champion-input" value={myMatch.role || ""} onChange={(e) => setMyMatch({...myMatch, role: e.target.value})}>
                      <option value="">-- Sélectionner --</option>
                      {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </div>
                  <div className="split-row">
                    <div className="split-col" style={{flex:2}}>
                      <label className="input-label">Mon Champion</label>
                      <input className="champion-input" value={myMatch.champion} onChange={(e) => handleMyChampInput(e.target.value)} placeholder="Ex: Yasuo" />
                      {suggestions.field === 'myChamp' && suggestions.list.length > 0 && (
                        <div className="suggestions-list">
                          {suggestions.list.map(c => (
                            <div key={c.id} className="suggestion-item" onClick={() => selectSuggestion('myChamp', c)}>
                              <img src={`https://ddragon.leagueoflegends.com/cdn/${version}/img/champion/${c.id}.png`} alt={c.name}/><span>{c.name}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="split-col" style={{flex:1}}>
                      <label className="input-label">LP (+/-)</label>
                      <input type="number" className="champion-input" value={lpChangeInput} onChange={(e) => setLpChangeInput(e.target.value)} />
                    </div>
                  </div>
                </div>
                <div className="section-title">Équipe Adverse (Game Précédente)</div>
                {ROLES.map(role => (
                  <div key={role} className="input-row">
                    <label className="input-label">{role}</label>
                    <input className={`champion-input ${hasDuplicates() && inputs[role] ? 'error' : ''}`} value={inputs[role]} onChange={(e) => handleEnemyInput(role, e.target.value)} placeholder={`Champion ${role}...`} autoComplete="off" />
                    {suggestions.field === role && suggestions.list.length > 0 && (
                      <div className="suggestions-list">
                        {suggestions.list.map(c => (
                          <div key={c.id} className="suggestion-item" onClick={() => selectSuggestion(role, c)}>
                            <img src={`https://ddragon.leagueoflegends.com/cdn/${version}/img/champion/${c.id}.png`} alt={c.name}/><span>{c.name}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                <button className="btn primary" style={{width:'100%', marginTop:'10px'}} onClick={saveHistory}>Enregistrer Partie</button>
              </div>
              <div className="history-column">
                <div className="stats-summary">
                  <div className="stat-box"><span className="stat-value">{stats.total}</span><span className="stat-label">Total</span></div>
                  <div className="stat-box"><span className="stat-value" style={{color:'var(--green-victory)'}}>{stats.wins}</span><span className="stat-label">Win</span></div>
                  <div className="stat-box"><span className="stat-value" style={{color:'var(--red-danger)'}}>{stats.losses}</span><span className="stat-label">Def</span></div>
                  <div className="stat-box"><span className="stat-value">{stats.winRate}%</span><span className="stat-label">Winrate</span></div>
                </div>
                <div className="data-controls">
                  <div className="btn-data" onClick={exportData}>Exporter JSON</div>
                  <label className="btn-data" style={{cursor:'pointer'}}>Importer JSON<input type="file" id="import-file" accept=".json" onChange={importData} /></label>
                </div>
                <div className="history-header">
                  <div className="history-info-left" style={{width:'70px'}}></div>
                  <div className="history-columns">
                    {ROLES.map(role => (<div key={role} className="history-cell"><img src={getRoleIconPath(role)} alt={role} className={`header-role-img ${historyFilter === role ? 'filter-active' : ''}`} title={`Filtrer par ${role}`} onClick={() => toggleFilter(role)} /></div>))}
                  </div>
                  <div style={{width:'34px'}}></div>
                </div>
                <div className="history-list">
                  {filteredHistory.length === 0 && <span style={{color:'#444', fontStyle:'italic', display:'block', textAlign:'center', marginTop:'20px'}}>Aucune partie.</span>}
                  {filteredHistory.map((game, index) => (
                    <div key={index} className={`history-item ${game.userResult ? (game.userResult === 'Win' ? 'win' : 'loss') : ''}`}>
                      <div className="history-info-left" style={{width:'70px', justifyContent:'center'}}>
                         {game.userRole ? <img src={getRoleIconPath(game.userRole)} className="played-role-icon" title={game.userRole} alt="" /> : <span>-</span>}
                         <span className="history-separator">|</span>
                         {game.userChamp ? (<img src={`https://ddragon.leagueoflegends.com/cdn/${version}/img/champion/${getUrlId(game.userChamp)}.png`} className="played-champ-icon" title={game.userChamp} alt="" />) : <span>-</span>}
                      </div>
                      <div className="history-columns">
                        {ROLES.map(r => {
                           const champId = game.enemyTeam ? game.enemyTeam[r] : "Garen";
                           return (<div key={r} className="history-cell"><img src={`https://ddragon.leagueoflegends.com/cdn/${version}/img/champion/${getUrlId(champId)}.png`} className="history-icon-img" title={`${r}: ${champId}`} alt={champId} onError={(e) => e.target.style.display='none'} /></div>);
                        })}
                      </div>
                      <button className="delete-btn" onClick={() => deleteHistoryItem(index)} title="Supprimer">×</button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
